import { migrateLegacyTopPositionsFilterState } from '@src/containers/Portfolio/PortfolioManagement/stateManagement/migrateLegacyTopPositionsFilterState';
import { getInitPortfolioViewState } from '@src/containers/Portfolio/PortfolioManagement/stateManagement/portfolioViewLayoutSlice';
import { portfolioViewStateSchema, type PortfolioViewState } from '@src/containers/Portfolio/PortfolioManagement/types';
import { DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT } from '@src/hooks/useFeatureFlag';
import type { AppConfigStateMethods } from '@src/providers/AppConfigProvider';
import type { AppConfigState } from '@src/providers/AppConfigProvider/AppConfigProvider.types';
import { OrgConfigurationKey, useOrgConfiguration } from '@src/providers/OrgConfigurationProvider';
import type { PortfolioSettingsContext } from '@src/providers/PortfolioSettingContext';
import { deserializeDateRangeOrLookback, logger, type DateRangeOrLookback } from '@talos/kyoko';
import { useCallback, useMemo, useRef, type ContextType } from 'react';

const defaultPortfolioViewState = getInitPortfolioViewState({});

function normalizeTopUnderlyingCount(raw: number): number {
  return raw >= 1 ? Math.floor(raw) : DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT;
}

/** Retrieve Portfolio View State from storage for {@see AppConfigState} */
export function usePortfolioAppConfigSettings(
  state: AppConfigState,
  methods: AppConfigStateMethods
): ContextType<typeof PortfolioSettingsContext> {
  const isValidatedRef = useRef(false);
  const { getConfig, isLoaded: isOrgConfigurationLoaded } = useOrgConfiguration();
  const defaultTopN = normalizeTopUnderlyingCount(
    getConfig(OrgConfigurationKey.PMSOverviewTopUnderlyingCount, DEFAULT_PMS_OVERVIEW_TOP_UNDERLYING_COUNT)
  );

  const validatePortfolioViewState = useCallback(
    (viewState: AppConfigState['portfolioViewState']): PortfolioViewState => {
      if (isValidatedRef.current) {
        return viewState;
      }
      // Finalize only after org config loads so enabled=true can re-migrate to the real Top N.
      if (isOrgConfigurationLoaded) {
        isValidatedRef.current = true;
      }
      try {
        // if we have any new properties added to the state since last this user logged in, we hydrate with our defaults.
        const hydratedInitialState = {
          ...defaultPortfolioViewState,
          // We switch from filter enabled to filter count, so we need to migrate the old behavior.
          // TODO: Remove this migration in UI 12.0
          ...migrateLegacyTopPositionsFilterState(viewState, defaultTopN),
        };

        const validated = portfolioViewStateSchema.validateSync(hydratedInitialState);
        let dateRangeToUse: DateRangeOrLookback | undefined = undefined;
        try {
          // deserialize the date range if it was set
          dateRangeToUse = validated.dateRange ? deserializeDateRangeOrLookback(validated.dateRange) : undefined;
        } catch (e) {
          if (e instanceof Error) {
            logger.error(
              new Error(
                `Error deserializing date range ${JSON.stringify(validated.dateRange)}: ${
                  e instanceof Error ? e.message : e
                }`,
                {
                  cause: e,
                }
              )
            );
          }
        }
        const validateWithDateRange = {
          ...validated,
          dateRange: dateRangeToUse,
        };
        return getInitPortfolioViewState(validateWithDateRange);
      } catch (e) {
        logger.error(
          new Error(
            `Error validating portfolio view state: ${e instanceof Error ? e.message : e}, defaulting to initial state`,
            { cause: e }
          )
        );
        // if validation fails, return the initial portfolio state so the user can still use the app
        return defaultPortfolioViewState;
      }
    },
    [defaultTopN, isOrgConfigurationLoaded]
  );

  return useMemo<ContextType<typeof PortfolioSettingsContext>>(() => {
    return {
      enablePMSLayout: state.enablePMSLayout,
      treatStablecoinsAsCash: state.treatStablecoinsAsCash,
      portfolioViewState: validatePortfolioViewState(state.portfolioViewState),
      setEnablePMSLayout: methods.setEnablePMSLayout,
      setTreatStablecoinsAsCash: methods.setTreatStablecoinsAsCash,
      setPortfolioViewState: methods.setPortfolioViewState,
    };
  }, [
    state.enablePMSLayout,
    state.treatStablecoinsAsCash,
    state.portfolioViewState,
    methods.setEnablePMSLayout,
    methods.setTreatStablecoinsAsCash,
    methods.setPortfolioViewState,
    validatePortfolioViewState,
  ]);
}
